/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 */

import React from 'react';
import localMessages, { loadLocale } from './index';

export const useLazyLocalMessages = () => {
  const [loadedLocalMessages, setLoadedLocalMessages] = React.useState(localMessages);
  const [requestedLocale, setRequestedLocale] = React.useState(null);
  const [failedLocales, setFailedLocales] = React.useState(() => new Set());
  const loadingLocales = React.useRef(new Set());
  const loadedLocalMessagesRef = React.useRef(loadedLocalMessages);
  const failedLocalesRef = React.useRef(failedLocales);

  loadedLocalMessagesRef.current = loadedLocalMessages;
  failedLocalesRef.current = failedLocales;

  const handleLocaleChange = React.useCallback(async locale => {
    if (!locale) {
      return;
    }

    const nextLocale = locale;

    setRequestedLocale(nextLocale);

    if (nextLocale === 'en-US') {
      return;
    }

    if (loadedLocalMessagesRef?.current?.[nextLocale]) {
      return;
    }

    if (failedLocalesRef?.current?.has?.(nextLocale)) {
      return;
    }

    if (loadingLocales?.current?.has?.(nextLocale)) {
      return;
    }

    loadingLocales?.current?.add?.(nextLocale);

    try {
      const localeMessages = await loadLocale(nextLocale);

      setLoadedLocalMessages(currentMessages => {
        if (currentMessages?.[nextLocale]) {
          return currentMessages;
        }

        return { ...currentMessages, [nextLocale]: localeMessages };
      });
    } catch (error) {
      setFailedLocales(currentFailed => {
        const nextFailed = new Set(currentFailed);

        nextFailed?.add?.(nextLocale);

        return nextFailed;
      });

      console.error(error);
    } finally {
      loadingLocales?.current?.delete?.(nextLocale);
    }
  }, []);

  const isLocaleReady =
    requestedLocale != null &&
    (requestedLocale === 'en-US' ||
      Boolean(loadedLocalMessages?.[requestedLocale]) ||
      failedLocales?.has?.(requestedLocale));

  return {
    loadedLocalMessages,
    handleLocaleChange,
    isLocaleReady,
    requestedLocale,
  };
};
